<script setup lang="ts">
import type { Provider } from "@lexical/yjs";
import { useDocumentVisibility, useWindowFocus } from "@vueuse/core";
import { $onUpdate, getDOMSelectionRange, getNearestEditorFromDOMNode, isHTMLElement } from "lexical";
import { CollaborationPluginV2__EXPERIMENTAL as LexicalCollaborationPlugin } from "lexical-vue";
import { h, onMounted, onUnmounted, render, shallowRef, watch } from "vue";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";

import { useDataStore, useEnvironmentStore, usePageStore, useUserStore } from "~/stores";

import CollaborationCursor from "../CollaborationCursor.vue";

const ONE_HOUR_IN_MS = 1000 * 60 * 60;

const props = defineProps<{
  id: string;
  namespace: string;
  cursorsContainerRef: HTMLElement | null;
}>();
const emit = defineEmits<{
  ready: [];
  disconnect: [];
  connectionError: [];
}>();

const dataStore = useDataStore();
const environmentStore = useEnvironmentStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const visibility = useDocumentVisibility();
const windowFocus = useWindowFocus();

const documentId = `${props.namespace}-${props.id}`;
const doc = new Y.Doc({ guid: documentId });
// TODO 2026-11-27: Re-enable BroadcastChannel once cache rebuilds cannot replay stale roots between tabs.
const provider: WebsocketProvider & Pick<Provider, "customCursorFn"> = new WebsocketProvider(
  `${environmentStore.wsBase}/ws/v0/texts`,
  documentId,
  doc,
  { connect: false, disableBc: true, params: { client_duid: pageStore.duid, collaboration_version: "2" } }
);
const cursors = new Set<HTMLElement>();
provider.customCursorFn = (cursor, caret) => {
  render(h(CollaborationCursor, { user: dataStore.getUserByDuid(cursor.name) }), caret);
  cursors.add(caret);
  return () => {
    render(null, caret);
    cursors.delete(caret);
  };
};
const root = shallowRef<Y.XmlElement | null>(null);
const getXmlElement = () => {
  const fragment = doc.getXmlFragment("lexical");
  const element = fragment.get(0);
  if (fragment.length !== 1 || !(element instanceof Y.XmlElement)) {
    throw new Error("The server did not initialize the collaboration root");
  }
  return element;
};
const onSync = (synced: boolean) => {
  if (synced) {
    root.value = getXmlElement();
  } else {
    emit("disconnect");
  }
};
const onConnectionError = () => emit("connectionError");

const onRemoteTransaction = (transaction: Y.Transaction) => {
  if (transaction.local || transaction.changed.size === 0) {
    return;
  }
  const container = props.cursorsContainerRef;
  const scroller = container?.closest<HTMLElement>(".dart-doc-scroll");
  const selection = window.getSelection();
  if (!scroller || !selection?.focusNode || !container?.contains(selection.focusNode)) {
    return;
  }
  const getTop = () => {
    const range = getDOMSelectionRange(selection, container);
    const node = range?.startContainer;
    const element = isHTMLElement(node) ? node : node?.parentElement;
    return (range?.getClientRects()[0] ?? element?.getBoundingClientRect())?.top;
  };
  const top = getTop();
  const viewport = scroller.getBoundingClientRect();
  if (top === undefined || top < viewport.top || top > viewport.bottom) {
    return;
  }
  // Capture before Yjs observers update Lexical, then restore after its DOM commit.
  getNearestEditorFromDOMNode(selection.focusNode)?.update(() =>
    $onUpdate(() => {
      const nextTop = getTop();
      if (nextTop !== undefined && container?.contains(selection.focusNode)) {
        scroller.scrollTop += nextTop - top;
      }
    })
  );
};

const disconnect = provider.disconnect.bind(provider);
provider.disconnect = () => {
  doc.off("beforeObserverCalls", onRemoteTransaction);
  provider.off("sync", onSync);
  provider.off("connection-error", onConnectionError);
  provider.off("connection-close", onConnectionError);
  disconnect();
};

let lastSyncedDt: Date | null = null;
const onReady = () => {
  lastSyncedDt = new Date();
  emit("ready");
};
const onUnidle = () => {
  if (lastSyncedDt && new Date().getTime() - lastSyncedDt.getTime() >= ONE_HOUR_IN_MS) {
    emit("disconnect");
  }
};
watch(visibility, (newVisibility) => {
  if (newVisibility !== "hidden") {
    onUnidle();
  }
});
watch(windowFocus, (newFocus) => {
  if (newFocus) {
    onUnidle();
  }
});

onMounted(() => {
  doc.on("beforeObserverCalls", onRemoteTransaction);
  provider.on("sync", onSync);
  provider.on("connection-error", onConnectionError);
  provider.on("connection-close", onConnectionError);
  provider.connect();
});
onUnmounted(() => {
  cursors.forEach((caret) => render(null, caret));
  cursors.clear();
  provider.destroy();
  doc.destroy();
});

defineExpose({ provider });
</script>

<template>
  <LexicalCollaborationPlugin
    v-if="root"
    :id="id"
    :doc="doc"
    :provider="provider as unknown as Provider"
    :get-xml-element="getXmlElement"
    :username="userStore.duid"
    :cursor-color="userStore.colorHex"
    :cursors-container-ref="cursorsContainerRef"
    @vue:mounted="onReady" />
</template>
